import { useState } from "react";
import type { ServiceConnections } from "@voice/api-contract";
import {
  equipmentIdentity,
  lineIdentity,
  type ConnectionFocus,
} from "./AccountConnections";

type Props = {
  data: ServiceConnections;
  commercial: boolean;
  busy: boolean;
  focus?: ConnectionFocus | undefined;
  save: (path: string, body: Record<string, unknown>) => Promise<void>;
  onLocations: () => void;
  onExtensions: () => void;
};
export function AssignmentEditor({
  data,
  commercial,
  busy,
  focus = {},
  save,
  onLocations,
  onExtensions,
}: Props) {
  const usableLines = data.lines.filter(
    (line) =>
      line.status !== "disconnected" &&
      (!commercial || line.extensionId !== null),
  );
  const devices = data.devices.filter(
    (device) => !["retired", "quarantined"].includes(device.status),
  );
  const focusNumber = data.numbers.find(
    (number) => number.id === focus.numberId,
  );
  const initialLine =
    usableLines.find((line) => line.id === focus.lineId) ??
    usableLines.find((line) => line.id === focusNumber?.lineId) ??
    (focus.deviceId &&
    usableLines.filter((line) => line.deviceId === focus.deviceId).length === 1
      ? usableLines.find((line) => line.deviceId === focus.deviceId)
      : undefined) ??
    (!focus.deviceId && usableLines.length === 1 ? usableLines[0] : undefined);
  const [lineId, setLineId] = useState(
    initialLine?.id ?? (!commercial ? "new" : ""),
  );
  const [deviceId, setDeviceId] = useState(
    focus.deviceId ??
      initialLine?.deviceId ??
      (devices.length === 1 ? devices[0]!.id : ""),
  );
  const [slot, setSlot] = useState(initialLine?.deviceSlot ?? 1);
  const [lineName, setLineName] = useState("Home telephone");
  const [numbers, setNumbers] = useState<string[]>(
    Array.from(
      new Set([
        ...data.numbers
          .filter((number) => initialLine && number.lineId === initialLine.id)
          .map((number) => number.id),
        ...(focus.numberId ? [focus.numberId] : []),
      ]),
    ),
  );
  const [outgoing, setOutgoing] = useState(initialLine?.outgoingNumberId ?? "");
  const [emergency, setEmergency] = useState(
    initialLine?.emergencyNumberId ?? "",
  );
  const line = usableLines.find((item) => item.id === lineId);
  const device = devices.find((item) => item.id === deviceId);
  const availableDevices = devices.filter(
    (item) => !line || item.locationId === line.locationId,
  );
  const eligibleNumbers = data.numbers.filter(
    (number) => number.eligible || (line && number.lineId === line.id),
  );
  const occupied = data.lines
    .filter((item) => item.id !== lineId && item.deviceId === deviceId)
    .map((item) => item.deviceSlot);
  const selectedNumbers = data.numbers.filter((number) =>
    numbers.includes(number.id),
  );
  const removedNumbers = data.numbers.filter(
    (number) =>
      line && number.lineId === line.id && !numbers.includes(number.id),
  );
  const location = data.locations.find(
    (item) => item.id === device?.locationId,
  );
  const canSave = Boolean(
    device?.version &&
    device.locationId &&
    availableDevices.some((item) => item.id === device.id) &&
    slot >= 1 &&
    slot <= device.slots &&
    !occupied.includes(slot) &&
    selectedNumbers.every((number) => number.eligible) &&
    (line || (!commercial && lineId === "new" && lineName.trim())),
  );
  const chooseLine = (id: string) => {
    const next = usableLines.find((item) => item.id === id);
    setLineId(id);
    setNumbers(
      Array.from(
        new Set([
          ...data.numbers
            .filter((number) => next && number.lineId === next.id)
            .map((number) => number.id),
          ...(focus.numberId ? [focus.numberId] : []),
        ]),
      ),
    );
    setOutgoing(next?.outgoingNumberId ?? "");
    setEmergency(next?.emergencyNumberId ?? "");
    if (next?.deviceId) {
      setDeviceId(next.deviceId);
      setSlot(next.deviceSlot ?? 1);
    } else if (next && device?.locationId !== next.locationId) setDeviceId("");
  };
  return (
    <section
      className="cr-panel cr-assignment-editor"
      aria-label="Complete telephone assignment"
    >
      <h3>Assign numbers and equipment</h3>
      <p>
        Choose what rings. The equipment, incoming numbers and caller-ID choices
        save together.
      </p>
      <form
        className="cr-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSave || !device || busy) return;
          const numberRoutes = [
            ...selectedNumbers.map((number) => ({
              numberId: number.id,
              expectedVersion: number.version,
              expectedLineId: number.lineId,
              expectedRecordedLineId: number.recordedLineId ?? null,
              action: "attach",
            })),
            ...removedNumbers.map((number) => ({
              numberId: number.id,
              expectedVersion: number.version,
              expectedLineId: number.lineId,
              expectedRecordedLineId: number.recordedLineId ?? null,
              action: "detach",
            })),
          ];
          void save("assignment", {
            target: line
              ? {
                  kind: "existing",
                  lineId: line.id,
                  expectedVersion: line.version,
                  expectedLegacyVersion: line.legacyVersion ?? "0",
                }
              : {
                  kind: "new_residential",
                  label: lineName.trim(),
                  locationId: device.locationId,
                },
            deviceId,
            deviceSlot: slot,
            expectedDeviceVersion: device.version,
            outgoingNumberId: outgoing || null,
            emergencyNumberId: emergency || null,
            numberRoutes,
          });
        }}
      >
        <fieldset className="cr-connection-fields" disabled={busy}>
          <legend>Number-to-phone connection</legend>
          <label className="cr-field">
            <span>{commercial ? "Extension" : "Telephone line"}</span>
            <select
              required
              value={lineId}
              onChange={(event) => chooseLine(event.target.value)}
            >
              <option value="">
                Choose {commercial ? "extension" : "line"}
              </option>
              {!commercial && (
                <option value="new">Create a line for this equipment</option>
              )}
              {usableLines.map((item) => (
                <option key={item.id} value={item.id}>
                  {lineIdentity(item)} · {item.locationName}
                </option>
              ))}
            </select>
          </label>
          {lineId === "new" && (
            <label className="cr-field">
              <span>Line name</span>
              <input
                value={lineName}
                onChange={(event) => setLineName(event.target.value)}
                required
                maxLength={120}
              />
            </label>
          )}
          <label className="cr-field">
            <span>Phone or adapter</span>
            <select
              required
              value={deviceId}
              onChange={(event) => {
                setDeviceId(event.target.value);
                const next = devices.find(
                  (item) => item.id === event.target.value,
                );
                const used = data.lines
                  .filter(
                    (item) => item.id !== lineId && item.deviceId === next?.id,
                  )
                  .map((item) => item.deviceSlot);
                setSlot(
                  Array.from(
                    { length: next?.slots ?? 1 },
                    (_, index) => index + 1,
                  ).find((value) => !used.includes(value)) ?? 1,
                );
              }}
            >
              <option value="">Choose equipment</option>
              {availableDevices.map((item) => (
                <option key={item.id} value={item.id}>
                  {equipmentIdentity(item)} ·{" "}
                  {data.locations.find((place) => place.id === item.locationId)
                    ?.name ?? "Location needed"}
                </option>
              ))}
            </select>
          </label>
          {device && (
            <label className="cr-field">
              <span>Physical port / SIP account</span>
              <select
                value={slot}
                onChange={(event) => setSlot(Number(event.target.value))}
              >
                {Array.from(
                  { length: device.slots },
                  (_, index) => index + 1,
                ).map((value) => (
                  <option
                    value={value}
                    key={value}
                    disabled={occupied.includes(value)}
                  >
                    Port {value}
                    {occupied.includes(value) ? " · Already assigned" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <fieldset className="cr-number-choices">
            <legend>Incoming numbers that ring here</legend>
            {eligibleNumbers.length ? (
              eligibleNumbers.map((number) => (
                <label className="cr-check" key={number.id}>
                  <input
                    type="checkbox"
                    checked={numbers.includes(number.id)}
                    onChange={(event) =>
                      setNumbers((current) =>
                        event.target.checked
                          ? [...current, number.id]
                          : current.filter((id) => id !== number.id),
                      )
                    }
                  />
                  <span>
                    {number.number}
                    {number.lineId && number.lineId !== lineId
                      ? ` · Currently ${data.lines.find((item) => item.id === number.lineId)?.label ?? "assigned elsewhere"}`
                      : ""}
                  </span>
                </label>
              ))
            ) : (
              <p>
                No eligible owned number is linked to this account. You can
                still assign an extension to equipment.
              </p>
            )}
          </fieldset>
          <label className="cr-field">
            <span>Outgoing caller ID</span>
            <select
              value={outgoing}
              onChange={(event) => setOutgoing(event.target.value)}
            >
              <option value="">Not selected</option>
              {data.numbers
                .filter((number) => number.eligible || number.id === outgoing)
                .map((number) => (
                  <option value={number.id} key={number.id}>
                    {number.number}
                  </option>
                ))}
            </select>
          </label>
          <label className="cr-field">
            <span>Emergency callback number</span>
            <select
              value={emergency}
              onChange={(event) => setEmergency(event.target.value)}
            >
              <option value="">Not selected</option>
              {data.numbers
                .filter((number) => number.eligible || number.id === emergency)
                .map((number) => (
                  <option value={number.id} key={number.id}>
                    {number.number}
                  </option>
                ))}
            </select>
          </label>
        </fieldset>
        {device?.recordedAssignments?.some(
          (item) => item.lineId !== lineId,
        ) && (
          <p className="cr-notice">
            Earlier system assignment:{" "}
            {device.recordedAssignments
              .map((item) => item.lineLabel)
              .join(", ")}
            . Its port is not recorded. Review it before reusing this equipment.
          </p>
        )}
        {device && !device.locationId && (
          <button
            type="button"
            className="cr-button secondary"
            onClick={onLocations}
          >
            Set the equipment's service location
          </button>
        )}
        {commercial && !usableLines.length && (
          <button
            type="button"
            className="cr-button secondary"
            onClick={onExtensions}
          >
            Create an extension
          </button>
        )}
        {device && (
          <div className="cr-assignment-review" aria-label="Assignment review">
            <strong>
              {selectedNumbers.map((number) => number.number).join(", ") ||
                "Internal calls only"}
            </strong>
            <p>
              {line ? lineIdentity(line) : lineName} →{" "}
              {equipmentIdentity(device)} → Port {slot}
            </p>
            <p>{location?.name ?? "Service location required"}</p>
            {removedNumbers.length > 0 && (
              <p>
                Remove incoming routing for:{" "}
                {removedNumbers.map((number) => number.number).join(", ")}.
                These numbers remain on the account.
              </p>
            )}
            <p className="cr-form-note">
              This saves the complete assignment. Activation, device
              registration and emergency routing require separate verification.
            </p>
          </div>
        )}
        <button className="cr-button" disabled={busy || !canSave}>
          {busy ? "Saving assignment…" : "Save complete assignment"}
        </button>
      </form>
    </section>
  );
}
